import styled, { keyframes } from "styled-components";
import React, { useContext, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from "formik";
import * as Yup from "yup";
import { HiOutlineUser, HiOutlineMail, HiOutlineLockClosed, HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';
import { IoClose, IoCheckmarkCircle } from 'react-icons/io5';
import { updateUser } from "../../services/api";
import { AuthContext } from "../../contexts/auth";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import CryptoJS from 'crypto-js';
import { getStoredUser } from '../../utils/safeStorage';

// ============================================
// ANIMAÇÕES
// ============================================
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -48%) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// ============================================
// STYLED COMPONENTS
// ============================================
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(8px);
  animation: ${fadeIn} 0.2s ease-out;
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1001;
  width: 95%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 20px;
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  animation: ${slideUp} 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  /* Scrollbar personalizada */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.2);
  }
`;

const ModalHeader = styled.div`
  position: relative;
  padding: 28px 32px 20px;
  border-bottom: 1px solid #f1f5f9;
  background: linear-gradient(135deg, rgba(49, 140, 213, 0.04) 0%, rgba(79, 172, 254, 0.02) 100%);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #fee2e2;
    border-color: #fecaca;
    color: #dc2626;
    transform: rotate(90deg);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Title = styled.h2`
  margin: 0 0 6px 0;
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.5px;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: #64748b;
`;

const ModalBody = styled.div`
  padding: 28px 32px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 16px;
    height: 16px;
    color: #94a3b8;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 16px;
  padding-right: ${({ $hasIcon }) => ($hasIcon ? '48px' : '16px')};
  font-size: 14px;
  color: #1e293b;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  outline: none;
  transition: all 0.2s ease;

  &::placeholder {
    color: #94a3b8;
  }

  &:hover {
    border-color: #cbd5e1;
  }

  &:focus {
    background: #ffffff;
    border-color: rgb(49, 140, 213);
    box-shadow: 0 0 0 4px rgba(49, 140, 213, 0.1);
  }

  &:disabled {
    background: #f1f5f9;
    cursor: not-allowed;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    color: #64748b;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ErrorMessage = styled.span`
  font-size: 12px;
  color: #dc2626;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgb(49, 140, 213);
    background: linear-gradient(135deg, rgba(49, 140, 213, 0.05) 0%, rgba(79, 172, 254, 0.03) 100%);
  }

  &.active {
    border-color: rgb(49, 140, 213);
    background: linear-gradient(135deg, rgba(49, 140, 213, 0.08) 0%, rgba(79, 172, 254, 0.04) 100%);
  }
`;

const CustomCheckbox = styled.div`
  width: 22px;
  height: 22px;
  min-width: 22px;
  border: 2px solid ${({ $checked }) => ($checked ? 'rgb(49, 140, 213)' : '#cbd5e1')};
  border-radius: 6px;
  background: ${({ $checked }) => ($checked ? 'rgb(49, 140, 213)' : '#ffffff')};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  svg {
    width: 14px;
    height: 14px;
    color: white;
    opacity: ${({ $checked }) => ($checked ? 1 : 0)};
    transform: ${({ $checked }) => ($checked ? 'scale(1)' : 'scale(0.5)')};
    transition: all 0.2s ease;
  }
`;

const CheckboxLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const PasswordSection = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px dashed #e2e8f0;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  max-height: ${({ $visible }) => ($visible ? '200px' : '0')};
  overflow: hidden;
  transition: all 0.3s ease;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 32px 28px;
  border-top: 1px solid #f1f5f9;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 48px;
  padding: 0 24px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const CancelButton = styled(Button)`
  background: #ffffff;
  color: #64748b;
  border: 2px solid #e2e8f0;

  &:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #475569;
  }
`;

const SaveButton = styled(Button)`
  background: linear-gradient(135deg, rgb(49, 140, 213) 0%, rgb(79, 172, 254) 100%);
  color: #ffffff;
  border: none;
  min-width: 160px;
  position: relative;
  overflow: hidden;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px -4px rgba(49, 140, 213, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &.loading {
    pointer-events: none;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.3),
        transparent
      );
      background-size: 200% 100%;
      animation: ${shimmer} 1.5s infinite;
    }
  }
`;

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
const ModalEditUser = ({
  setEditUser,
  showEditUser,
  _idUserEdit,
  usernameEdit,
  emailEdit,
  editPerfil,
}) => {
  const { t } = useTranslation('common');
  const { user } = useContext(AuthContext);
  const [resetPassword, setResetPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const modalRef = useRef(null);

  // Validação com i18n
  const SignUpSchema = Yup.object().shape({
    email: Yup.string()
      .email(t('editProfile.invalidEmail'))
      .required(t('editProfile.requiredField')),
    password: resetPassword
      ? Yup.string()
          .required(t('editProfile.requiredField'))
          .min(6, t('editProfile.passwordMinLength'))
      : Yup.string(),
    nome: Yup.string().required(t('editProfile.requiredField')),
  });

  const formik = useFormik({
    validationSchema: SignUpSchema,
    validateOnBlur: true,
    validateOnChange: false,
    enableReinitialize: true,
    initialValues: {
      email: emailEdit,
      password: "",
      nome: usernameEdit,
    },
    onSubmit: (values) => {
      const email = values.email;
      const password = values.password;
      const name = values.nome;

      if (password.length < 6 && resetPassword) {
        toast.warning(t('editProfile.passwordMinLength'), { autoClose: 3000 });
        return;
      }

      handleSubmitAPI(_idUserEdit, email, password, name);
    },
  });

  // Chave de criptografia
  const secretKey = 'my-custom-secret-key:1af0addcce4b56f753ed10c285667ad031bae57af19747f576b05312b8cf85be';

  const handleSubmitAPI = async (_idUserEdit, email, password, nome) => {
    setIsLoading(true);

    try {
      const cast = await updateUser(email, password, nome);

      if (cast.status === 200) {
        if (editPerfil) {
          // Atualizar localStorage
          let existing = getStoredUser();
          existing.email = email;
          existing.name = nome;

          const encryptedUser = CryptoJS.AES.encrypt(
            JSON.stringify(existing),
            secretKey
          ).toString();
          localStorage.setItem("user", encryptedUser);
        }

        toast.success(t('editProfile.successMessage'), { autoClose: 3000 });
        setEditUser(!showEditUser);
      } else {
        toast.error(cast.data.msg || t('editProfile.errorMessage'), { autoClose: 5000 });
      }
    } catch (err) {
      toast.error(err?.response?.data?.msg || t('editProfile.errorMessage'), { autoClose: 5000 });
    } finally {
      setIsLoading(false);
    }
  };

  const closeEditUser = () => {
    setEditUser(!showEditUser);
    setResetPassword(false);
    formik.resetForm();
  };

  // Fechar ao clicar fora
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeEditUser();
    }
  };

  // Fechar com ESC
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !showEditUser) {
        closeEditUser();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showEditUser]);

  const isOpen = !showEditUser;

  return (
    <Overlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContainer ref={modalRef} onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <CloseButton onClick={closeEditUser} type="button">
            <IoClose />
          </CloseButton>
          <Title>{t('editProfile.title')}</Title>
          <Subtitle>{t('editProfile.subtitle')}</Subtitle>
        </ModalHeader>

        <form onSubmit={formik.handleSubmit}>
          <ModalBody>
            <FormGrid>
              {/* Campo Nome */}
              <InputGroup>
                <Label>
                  <HiOutlineUser />
                  {t('editProfile.name')}
                </Label>
                <Input
                  id="nome"
                  name="nome"
                  type="text"
                  placeholder={t('editProfile.namePlaceholder')}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.nome}
                />
                {formik.touched.nome && formik.errors.nome && (
                  <ErrorMessage>{formik.errors.nome}</ErrorMessage>
                )}
              </InputGroup>

              {/* Campo Email */}
              <InputGroup>
                <Label>
                  <HiOutlineMail />
                  {t('editProfile.email')}
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder={t('editProfile.emailPlaceholder')}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                />
                {formik.touched.email && formik.errors.email && (
                  <ErrorMessage>{formik.errors.email}</ErrorMessage>
                )}
              </InputGroup>

              {/* Checkbox Redefinir Senha */}
              <InputGroup className="full-width">
                <Label>{t('editProfile.resetPasswordQuestion')}</Label>
                <CheckboxContainer
                  className={resetPassword ? 'active' : ''}
                  onClick={() => setResetPassword(!resetPassword)}
                >
                  <CustomCheckbox $checked={resetPassword}>
                    <IoCheckmarkCircle />
                  </CustomCheckbox>
                  <CheckboxLabel>{t('editProfile.resetPasswordYes')}</CheckboxLabel>
                </CheckboxContainer>
              </InputGroup>

              {/* Campo Nova Senha */}
              <PasswordSection $visible={resetPassword}>
                <InputGroup>
                  <Label>
                    <HiOutlineLockClosed />
                    {t('editProfile.newPassword')}
                  </Label>
                  <InputWrapper>
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('editProfile.newPasswordPlaceholder')}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.password}
                      $hasIcon
                      disabled={!resetPassword}
                    />
                    <PasswordToggle
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? <HiOutlineEyeOff /> : <HiOutlineEye />}
                    </PasswordToggle>
                  </InputWrapper>
                  {formik.touched.password && formik.errors.password && (
                    <ErrorMessage>{formik.errors.password}</ErrorMessage>
                  )}
                </InputGroup>
              </PasswordSection>
            </FormGrid>
          </ModalBody>

          <ModalFooter>
            <CancelButton type="button" onClick={closeEditUser}>
              <IoClose />
              {t('editProfile.cancel')}
            </CancelButton>
            <SaveButton
              type="submit"
              className={isLoading ? 'loading' : ''}
              disabled={isLoading}
            >
              <IoCheckmarkCircle />
              {isLoading ? t('messages.saving') : t('editProfile.saveChanges')}
            </SaveButton>
          </ModalFooter>
        </form>
      </ModalContainer>
    </Overlay>
  );
};

export default ModalEditUser;
